import React, { useEffect, useState } from "react";
import BaseModalAnimated from "../../components/modal/BaseModalAnimated";
import CustomButton from "../../components/form/CustomButton";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  configurationsAlertsResource,
  usersResource,
} from "../../helpers/configRoutes/backend_helper";
import { showFormErrors } from "../../helpers/showFormErrors";
import { useSelector } from "react-redux";
import Select from "react-select";

const AddUpConfigurationAlert = ({ modal, setModal, setFilters, filters }) => {
  const { validations, notification_alert } = useSelector(
    (state) => state.NotificationAlert
  );

  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);

  // Catálogo de usuarios para extra_users
  useEffect(() => {
    const getCatalogs = async () => {
      try {
        const response = await usersResource("get", {
          params: { select: true },
        });
        if (response.status && response.users) {
          setUsers(response.users);
        }
      } catch (error) {
        console.error("Error obtaining users:", error);
      }
    };

    if (modal.show) {
      getCatalogs();
    }
  }, [modal.show]);

  // Formik
  const validation = useFormik({
    initialValues: notification_alert,
    enableReinitialize: true,
    validationSchema: Yup.object()
      .shape({
        ...validations,
      })
      .test({
        name: "at-least-one",
        message: "You must select at least one truck_id or trailer_id",
        test: function (values) {
          return values.truck_id || values.trailer_id;
        },
      }),
    onSubmit: async (values, { setErrors }) => {
      setLoading(true);

      const response = await configurationsAlertsResource(
        "post",
        values,
        values.id
      );

      if (response.status) {
        setFilters({ ...filters, search: filters.search });
        setModal({ ...modal, show: false });
      } else {
        setErrors(response.errors || {});
      }

      setLoading(false);
    },
  });

  // Opciones para el Select de usuarios extra
  const userOptions = users.map((user) => ({
    value: user.id,
    label:
      (user.first_name || user.last_name)
        ? `${user.first_name || ""} ${user.last_name || ""}`.trim()
        : user.name || `User #${user.id}`,
  }));

  const extraUsersValue = (validation.values.extra_users || []).map((u) => ({
    value: u.id,
    label:
      (u.first_name || u.last_name)
        ? `${u.first_name || ""} ${u.last_name || ""}`.trim()
        : u.name || `User #${u.id}`,
  }));

  return (
    <BaseModalAnimated
      modal={modal}
      setModal={setModal}
      title={modal.title || "Configuration alerts"}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          validation.handleSubmit();
          showFormErrors(validation);
          return false;
        }}
        className="space-y-6"
      >
        {/* SECCIÓN 1: Destinatarios */}
        <section className="space-y-3">
          <div className="flex items-center gap-2">
            <span className="inline-flex w-6 h-6 items-center justify-center rounded-full bg-cs-primary-soft text-xs">
              👤
            </span>
            <h3 className="text-sm font-semibold text-cs-dark">
              Users
            </h3>
          </div>
          <p className="text-[11px] text-cs-muted">
            Choose who should receive alerts for this configuration.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs text-cs-dark">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                name="user_default"
                className="w-3.5 h-3.5 accent-cs-primary"
                checked={!!validation.values.user_default}
                onChange={() =>
                  validation.setFieldValue(
                    "user_default",
                    !validation.values.user_default
                  )
                }
              />
              <span className="flex flex-col">
                <span>Notify the user who triggered the alert</span>
                <span className="text-[10px] text-cs-muted">
                  This sends the notification to the user who performed the related action.
                </span>
              </span>
            </label>

            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                name="company_administrator"
                className="w-3.5 h-3.5 accent-cs-primary"
                checked={!!validation.values.company_administrator}
                onChange={() =>
                  validation.setFieldValue(
                    "company_administrator",
                    !validation.values.company_administrator
                  )
                }
              />
              <span>Send notification to company administrators</span>
            </label>
          </div>

          <div className="mt-3">
            <label className="block text-[11px] font-medium text-cs-dark mb-1.5">
              Extra users
            </label>
            <Select
              isMulti
              name="extra_users"
              classNamePrefix="cs-select"
              options={userOptions}
              value={extraUsersValue}
              onChange={(selected) => {
                const mapped = (selected || []).map((sel) => ({
                  id: sel.value,
                  // Si quieres puedes guardar solo id, de momento mantengo estructura similar
                  first_name: sel.label.split(" ")[0],
                  last_name: sel.label.split(" ").slice(1).join(" "),
                }));
                validation.setFieldValue("extra_users", mapped);
              }}
              styles={{
                control: (base) => ({
                  ...base,
                  minHeight: 34,
                  borderColor: "#E5E7EB",
                  boxShadow: "none",
                  fontSize: "11px",
                }),
                valueContainer: (base) => ({
                  ...base,
                  padding: "2px 6px",
                }),
                multiValue: (base) => ({
                  ...base,
                  backgroundColor: "#FFF4E5",
                }),
                multiValueLabel: (base) => ({
                  ...base,
                  color: "#732E00",
                  fontSize: "10px",
                }),
                multiValueRemove: (base) => ({
                  ...base,
                  color: "#732E00",
                  ":hover": { backgroundColor: "#FFE0B8", color: "#732E00" },
                }),
                menu: (base) => ({
                  ...base,
                  fontSize: "11px",
                  zIndex: 9999,
                }),
              }}
              placeholder="Select extra users..."
            />
          </div>
        </section>

        <hr className="border-cs-border/70" />

        {/* SECCIÓN 2: Canales */}
        <section className="space-y-3">
          <div className="flex items-center gap-2">
            <span className="inline-flex w-6 h-6 items-center justify-center rounded-full bg-cs-primary-soft text-xs">
              📡
            </span>
            <h3 className="text-sm font-semibold text-cs-dark">
              Notification channels
            </h3>
          </div>
          <p className="text-[11px] text-cs-muted">
            Choose through which channels alerts will be delivered.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs text-cs-dark">
            {[
              { name: "email", label: "Email" },
              { name: "sokets", label: "Real-time dashboard" },
              { name: "sms", label: "SMS" },
            ].map(({ name, label }) => (
              <label key={name} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  name={name}
                  className="w-3.5 h-3.5 accent-cs-primary"
                  checked={!!validation.values[name]}
                  onChange={() =>
                    validation.setFieldValue(
                      name,
                      !validation.values[name]
                    )
                  }
                />
                <span>{label}</span>
              </label>
            ))}
          </div>
        </section>

        {/* FOOTER */}
        <div className="pt-4 flex justify-end">
          <CustomButton
            loading={loading}
            textLoad="Saving..."
            type="submit"
            className="
              px-6 py-2.5 rounded-xl
              bg-cs-primary hover:bg-cs-primary-hover
              text-white text-xs font-medium
              shadow-md hover:shadow-lg
              transition-all
            "
          >
            Save configuration
          </CustomButton>
        </div>
      </form>
    </BaseModalAnimated>
  );
};

export default AddUpConfigurationAlert;
